import {RSHttpStatusCodes} from '../data/http-status-codes';
import {RSFormHelper} from '../helpers/form';
import {RSDispatcher} from '../utils/dispatcher';


export class RSUrlRequest extends RSDispatcher
{
	static get EVENT_ERROR() { return 'error'; };
	static get EVENT_COMPLETE() { return 'complete'; };


	/**
	 * @param {boolean} addIdentifier
	 */
	constructor(addIdentifier = true)
	{
		super();

		this.addIdentifier = addIdentifier;
		this.logErrors = ((typeof ENVIRONMENT !== 'undefined') && (ENVIRONMENT !== 'production'));

		let warningAlreadyDisplayed = document.body.getAttribute('data-rs-logs-warned');
		warningAlreadyDisplayed = ((warningAlreadyDisplayed != null) && (parseInt(warningAlreadyDisplayed) === 1));
		
		if (this.logErrors && !warningAlreadyDisplayed && (typeof console !== 'undefined') && (typeof console.warn !== 'undefined'))
		{
			document.body.setAttribute('data-rs-logs-warned', '1'); // Pour ne pas avoir le warning 100 fois dans la console
			
			console.warn('Warning: Error logs are enabled in RSUrlRequest!');
		}
	}


	//
	// Public Methods
	//

	/**
	 * @param {string} url
	 */
	get(url)
	{
		const _class = this;

		if (this.addIdentifier)
		{
			url = url + ((/\?/).test(url) ? '&' : '?') + '_is_fetchjs=true';
		}

		fetch(url, {
			headers: {
				'Accept': 'application/json',
				'X-Requested-With': 'XMLHttpRequest'
			},
			mode: 'cors',
			cache: 'no-cache'
		})
			.then(response => {
				return _class._manageRequestStatus(response);
			})
			.then(response => {
				return response.json();
			})
			.then(data => {
				_class.dispatch(RSUrlRequest.EVENT_COMPLETE, {
					data: data,
					error: null
				});
			})
			.catch(error => {
				if (_class.logErrors && (typeof console !== 'undefined') && (typeof console.warn !== 'undefined'))
				{
					let errorCode, errorText;

					if ((typeof error.statusCode !== 'undefined') && (typeof error.statusText !== 'undefined'))
					{
						errorCode = error.statusCode;
						errorText = error.statusText;
					}
					else
					{
						errorCode = RSHttpStatusCodes.METHOD_FAILURE;
						errorText = error;
					}

					console.warn(`RSUrlRequest.get()\nError ${errorCode} at ${url}`);
					console.warn(errorText);

					if (typeof error.body !== 'undefined')
					{
						error.body.then((text) => {
							console.warn(text);
						});
					}
				}

				_class.dispatch(RSUrlRequest.EVENT_ERROR, {
					data: null,
					error: {
						statusCode: RSHttpStatusCodes.METHOD_FAILURE,
						statusText: error
					}
				});
			});
	}


	/**
	 * @param {string} url
	 * @param {Object} data
	 * @param {boolean} sendAsFormUrlEncoded
	 * @param {boolean} returnPostedData
	 */
	postData(url, data, sendAsFormUrlEncoded = true, returnPostedData = false)
	{
		if (this.addIdentifier) data._is_fetchjs = true;

		const _class = this;
		const _data = data;

		let contentType,
			body;

		if (sendAsFormUrlEncoded)
		{
			contentType = 'application/x-www-form-urlencoded';

			const urlEncodedDataPairs = [];

			for (const key in data)
			{
				if (data.hasOwnProperty(key))
				{
					urlEncodedDataPairs.push(encodeURIComponent(key) + '=' + encodeURIComponent(data[key]));
				}
			}

			body = urlEncodedDataPairs.join('&').replace(/%20/g, '+');
		}
		else
		{
			contentType = 'application/json';
			body = JSON.stringify(data);
		}

		fetch(url, {
			method: 'post',
			headers: {
				'Accept': 'application/json',
				'Content-type': `${contentType}; charset=utf-8`,
				'X-Requested-With': 'XMLHttpRequest'
			},
			mode: 'cors',
			cache: 'no-cache',
			body: body
		})
			.then(response => {
				return _class._manageRequestStatus(response);
			})
			.then(response => {
				return response.json();
			})
			.then(data => {
				let output = {
					data: data,
					error: null
				};

				if (returnPostedData)
				{
					output.posted_data = {
						object: _data,
						formatted: body
					};
				}

				_class.dispatch(RSUrlRequest.EVENT_COMPLETE, output);
			})
			.catch(error => {
				if (_class.logErrors && (typeof console !== 'undefined') && (typeof console.warn !== 'undefined'))
				{
					let errorCode, errorText;

					if ((typeof error.statusCode !== 'undefined') && (typeof error.statusText !== 'undefined'))
					{
						errorCode = error.statusCode;
						errorText = error.statusText;
					}
					else
					{
						errorCode = RSHttpStatusCodes.METHOD_FAILURE;
						errorText = error;
					}

					console.warn(`RSUrlRequest.postData()\nError ${errorCode} at ${url}`);
					console.warn(errorText);

					if (typeof error.body !== 'undefined')
					{
						error.body.then((text) => {
							console.warn(text);
						});
					}
				}

				_class.dispatch(RSUrlRequest.EVENT_ERROR, {
					data: null,
					error: {
						statusCode: RSHttpStatusCodes.METHOD_FAILURE,
						statusText: error
					}
				});
			});
	}


	/**
	 * @param {string} url
	 * @param {FormData} formData
	 * @param {boolean} returnPostedData
	 */
	postFormData(url, formData, returnPostedData = false)
	{
		if (this.addIdentifier) formData.append('_is_fetchjs', true);

		const _class = this;
		const _formData = formData;

		fetch(url, {
			method: 'POST',
			headers: {
				'Accept': 'application/json',
				'X-Requested-With': 'XMLHttpRequest',
			},
			mode: 'cors',
			cache: 'no-cache',
			body: formData
		})
			.then(response => {
				return _class._manageRequestStatus(response);
			})
			.then(response => {
				return response.json();
			})
			.then(data => {
				let output = {
					data: data,
					error: null
				};

				if (returnPostedData)
				{
					output.posted_data = {
						object: _formData,
						formatted: RSFormHelper.jsonifyFormData(_formData)
					};
				}

				_class.dispatch(RSUrlRequest.EVENT_COMPLETE, output);
			})
			.catch(error => {
				if (_class.logErrors && (typeof console !== 'undefined') && (typeof console.warn !== 'undefined'))
				{
					let errorCode, errorText;

					if ((typeof error.statusCode !== 'undefined') && (typeof error.statusText !== 'undefined'))
					{
						errorCode = error.statusCode;
						errorText = error.statusText;
					}
					else
					{
						errorCode = RSHttpStatusCodes.METHOD_FAILURE;
						errorText = error;
					}

					console.warn(`RSUrlRequest.postFormData()\nError ${errorCode} at ${url}`);
					console.warn(errorText);

					if (typeof error.body !== 'undefined')
					{
						error.body.then((text) => {
							console.warn(text);
						});
					}
				}

				_class.dispatch(RSUrlRequest.EVENT_ERROR, {
					data: null,
					error: {
						statusCode: RSHttpStatusCodes.METHOD_FAILURE,
						statusText: error
					}
				});
			});
	}


	//
	// Private Methods
	//

	/**
	 * @param {Object} response
	 * @returns {Promise<any>}
	 * @private
	 */
	_manageRequestStatus(response)
	{
		if ((response.status >= 200) && (response.status < 300))
		{
			return Promise.resolve(response);
		}
		else
		{
			/*response.text().then((text) => {
				console.log(text);
			});*/

			return Promise.reject({
				statusCode: response.status,
				statusText: response.statusText,
				body: response.text() // response.json()
			});
		}
	}
}
